'use client';

import { useState, useEffect, Suspense, useMemo } from 'react';
import { useSearchParams } from 'next/navigation';
import { cn } from '@/shadcn/lib/utils';
import { useRouter } from 'next/navigation';
import { Input } from '@/shared/components/ui/Input';
import { Button } from '@/shared/components/ui/Button';
import { SearchIcon } from 'lucide-react';

type TopFilterLinkKey = 'agencies' | 'brokers' | 'developers';

type TopFilterLink = {
    key: TopFilterLinkKey;
    text: string;
    onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
    position: 'left' | 'right';
    imgSrc?: string;
    href?: string;
};

const TOP_LINKS: Array<TopFilterLink> = [
    {
        key: 'agencies',
        text: 'Realitné kancelárie',
        href: '/kancelarie',
        position: 'right',
        imgSrc: '/images/ico_developeri.svg',
    },
    {
        key: 'brokers',
        text: 'Realitní markéri',
        href: '/makleri',
        position: 'right',
        imgSrc: '/images/ico_makleri.svg',
    },
    {
        key: 'developers',
        text: 'Developeri',
        href: '/developeri',
        position: 'right',
        imgSrc: '/images/ico_rk.svg',
    },
];

const FILTER_CHECKBOXES = [
    {
        key: 'narks',
        text: 'je členom NARKS',
    },
    {
        key: 'real-unie',
        text: 'je členom Realitnej únie SR',
    },
    {
        key: 'zrks',
        text: 'je členom ZRKS',
    },
];

interface Props {
    entityType: 'brokers' | 'agencies' | 'developers';
}

export function RealtorsFilter(props: Props) {
    const router = useRouter();

    // state
    const [searchParams, setSearchParams] = useState(new URLSearchParams({}));
    const [selectedMemberships, setSelectedMemberships] = useState<string[]>([]);
    const [search, setSearch] = useState('');

    const topLinks = useMemo(() => {
        return TOP_LINKS.map((i) => ({
            jsx: (
                <a
                    href={i.href ?? '#'}
                    className={cn(
                        `py-[9px] px-[13px] bg-[rgba(255,255,255,.5)] text-[11px] font-[500] rounded-[30px] whitespace-nowrap w-full text-center`,
                        props.entityType === i.key &&
                            'bg-[#fff] shadow-[0_11px_27px_0_rgba(0,0,0,0.15)]',
                        'sm:text-[13px]',
                        'lg:w-[auto] lg:min-w-[100px]',
                        'xl:text-[14px] xl:px-[17px]',
                    )}
                    onClick={(e) => {
                        if (i.href) {
                            router.push(i.href);
                            return;
                        } else {
                            e.preventDefault();
                            i.onClick?.(e);
                        }
                    }}
                >
                    {i.text}
                </a>
            ),
            position: i.position,
        }));
    }, [TOP_LINKS]);

    return (
        <>
            <Suspense>
                <GetSearchParams onSearchParams={(params) => setSearchParams(params)} />
            </Suspense>
            <div className="w-full">
                {/* Top nav */}
                <div
                    className="lg:flex lg:items-center lg:justify-statt
                "
                >
                    <div className="flex gap-[7px] xl:gap-[10px]">
                        {topLinks ? (
                            topLinks.map((link, i) => (
                                <div key={i} className="flex flex-1 lg:grow-0">
                                    {link.jsx}
                                </div>
                            ))
                        ) : (
                            <></>
                        )}
                    </div>
                </div>
                {/* Filter */}
                <div
                    className={cn(
                        'mt-[10px] bg-[#fff] py-[20px] px-[15px] rounded-[18px] shadow-[0_11px_30px_0_rgba(0,0,0,0.15)]',
                        'lg:p-[25px] xl:p-[35px] xl:rounded-[25px]',
                        'md:flex md:items-center md:gap-[25px] md:p-[30px]',
                        'lg:p-[35px] lg:mt-[12px]',
                        'xl:p-[40px] xl:mt-[15px]',
                    )}
                >
                    {/* Search input */}
                    <div
                        className={cn(
                            'flex items-center gap-[10px]',
                            'md:order-2 md:flex-1',
                            'xl:gap-[15px]',
                        )}
                    >
                        <div className="flex-1 flex items-center relative">
                            <SearchIcon
                                className={cn(
                                    'absolute left-[10px] size-[16px] text-secondary',
                                    'md:left-[15px] md:size-[18px]',
                                    'xl:left-[25px]',
                                )}
                            />
                            <Input
                                className={cn(
                                    `rounded-[40px] min-h-[40px] rounded-[40px] pl-[33px] text-[12px]`,
                                    `focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus-visible:shadow-none`,
                                    'md:min-h-[45px] md:pl-[42px] md:text-[13px]',
                                    'lg:min-h-[50px]',
                                    'xl:pl-[52px]',
                                )}
                                placeholder="Nájdi svojho experta podľa mena, názvu alebo miesta"
                            />
                        </div>
                        <div className="relative">
                            <Button
                                color="black"
                                className="px-[12px] size-[40px] lg:size-[auto]"
                                onClick={() => {}}
                            >
                                <SearchIcon className="size-[18px] text-[#fff]" />
                                <p className="hidden lg:block text-[13px]">Vyhľadať</p>
                            </Button>
                        </div>
                    </div>
                    {/* Memberships */}
                    <div
                        className={cn(
                            'flex flex-col gap-[2px] mt-[15px] md:mt-[0]',
                            'lg:gap-[5px]',
                        )}
                    >
                        {FILTER_CHECKBOXES.map((checkbox) => (
                            <a
                                href="#"
                                className="flex items-center gap-[10px] py-[2px]"
                                onClick={() => {
                                    if (selectedMemberships.includes(checkbox.key)) {
                                        setSelectedMemberships((s) =>
                                            s.filter((i) => i !== checkbox.key),
                                        );
                                    } else {
                                        setSelectedMemberships((s) => [...s, checkbox.key]);
                                    }
                                }}
                            >
                                <div className="size-[14px] flex items-center justify-center border rounded-full">
                                    {selectedMemberships.includes(checkbox.key) && (
                                        <div className="size-[8px] bg-[#000] rounded-full"></div>
                                    )}
                                </div>
                                <div>
                                    <p
                                        className={`text-[13px] font-[500] ${selectedMemberships.includes(checkbox.key) ? '' : 'text-secondary'}`}
                                    >
                                        {checkbox.text}
                                    </p>
                                </div>
                            </a>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
}

function GetSearchParams({
    onSearchParams,
}: {
    onSearchParams: (params: URLSearchParams) => void;
}) {
    const searchParams = useSearchParams();
    useEffect(() => {
        if (searchParams) {
            onSearchParams(searchParams);
        }
    }, [searchParams]);
    return <></>;
}
